import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

function ConfigEditor() {
  const [config, setConfig] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [jsonError, setJsonError] = useState(null);

  const storedConfig = JSON.parse(localStorage.getItem('qianshu_config') || '{}');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getConfig(storedConfig.appId);
      // 确保 config 是有效的对象
      const configData = data.data?.config || {};
      setConfig(JSON.stringify(configData, null, 2));
    } catch (err) {
      setError(err.message);
      setConfig('{}');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (value) => {
    setConfig(value);
    setSuccess(false);
    
    // 验证 JSON
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  const handleSave = async () => {
    try {
      // 验证 JSON
      const configObj = JSON.parse(config);
      
      setSaving(true);
      setError(null);
      setSuccess(false);

      await apiClient.updateConfig(storedConfig.appId, configObj, comment);
      
      setSuccess(true);
      setComment('');
      
      // 3秒后清除成功提示
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">配置编辑器</h1>
        <button
          onClick={loadConfig}
          className="btn-secondary flex items-center"
          disabled={loading}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          刷新
        </button>
      </div>

      {error && (
        <div className="card bg-red-50 border-red-200">
          <div className="flex items-center text-red-800">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="card bg-green-50 border-green-200">
          <div className="flex items-center text-green-800">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span>配置保存成功！新版本已生效。</span>
          </div>
        </div>
      )}

      <div className="card">
        <div className="mb-4">
          <label className="label">版本备注（可选）</label>
          <input
            type="text"
            className="input"
            placeholder="例如：更新了玩家上限配置"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="label mb-0">配置内容（JSON 格式）</label>
            {jsonError && (
              <span className="text-sm text-red-600">❌ JSON 格式错误</span>
            )}
            {!jsonError && config && (
              <span className="text-sm text-green-600">✓ JSON 格式正确</span>
            )}
          </div>
          <textarea
            className="input font-mono text-sm"
            rows="20"
            value={config}
            onChange={(e) => handleConfigChange(e.target.value)}
            spellCheck="false"
          />
          {jsonError && (
            <p className="mt-2 text-sm text-red-600">{jsonError}</p>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleSave}
            className="btn-primary flex items-center"
            disabled={saving || !!jsonError || !config}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? '保存中...' : '保存配置'}
          </button>
        </div>
      </div>

      <div className="card bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2">💡 提示</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• 配置必须是有效的 JSON 格式</li>
          <li>• 保存后会自动创建新版本，旧版本将被保存到历史记录</li>
          <li>• Unity 客户端会在下次请求时获取最新配置</li>
          <li>• 如果配置有误，可以在"版本历史"页面回滚到之前的版本</li>
        </ul>
      </div>
    </div>
  );
}

export default ConfigEditor;
